import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

function BrowseListings() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('suggested');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState('');
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [distance, setDistance] = useState(50);

  const categories = [
    { name: 'Vehicles', value: 'vehicles' },
    { name: 'Property Rentals', value: 'property-rentals' },
    { name: 'Apparel', value: 'apparel' },
    { name: 'Classifieds', value: 'classifieds' },
    { name: 'Electronics', value: 'electronics' },
    { name: 'Entertainment', value: 'entertainment' },
    { name: 'Family', value: 'family' },
    { name: 'Free Stuff', value: 'free-stuff' },
    { name: 'Outdoor Living', value: 'outdoor-living' },
    { name: 'Crafts', value: 'crafts' },
    { name: 'Home Decor', value: 'home-decor' },
    { name: 'Building Materials', value: 'building-materials' },
    { name: 'Property Sales', value: 'property-sales' },
    { name: 'Musical Instruments', value: 'musical-instruments' },
    { name: 'Office Equipment', value: 'office-equipment' },
    { name: 'Pets', value: 'pets' },
    { name: 'Sporting Goods', value: 'sporting-goods' },
    { name: 'Toys and Games', value: 'toys-and-games' },
    { name: 'Events', value: 'events' },
  ];

  useEffect(() => {
    fetchUserLocation();
    fetchListings();
  }, [sortBy, minPrice, maxPrice, searchTerm, selectedCategory, userLat, userLng, distance]);

  const fetchUserLocation = () => {
    if (user && user.location && user.location.city && user.location.state) {
      // Use stored user location
      return;
    }
    // Try geolocation API
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLat(position.coords.latitude);
          setUserLng(position.coords.longitude);
        },
        () => {
          // Geolocation failed, try IP-based
          fetch('https://ip-api.com/json/?fields=lat,lon')
            .then(res => res.json())
            .then(data => {
              if (data.lat && data.lon) {
                setUserLat(data.lat);
                setUserLng(data.lon);
              }
            })
            .catch(() => {});
        }
      );
    }
  };

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        sortBy,
        minPrice: minPrice > 0 ? minPrice : undefined,
        maxPrice: maxPrice ? maxPrice : undefined,
      };

      if (userLat && userLng) {
        params.userLat = userLat;
        params.userLng = userLng;
        params.distance = distance;
      }

      const response = await api.get('/api/listings', { params });

      if (response.data.success) {
        setListings(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching listings:', error);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-72 bg-white shadow-lg p-6 flex-shrink-0 min-h-screen">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Filters</h2>

        {/* Search */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchListings()}
            placeholder="Search listings..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm"
          />
        </div>


        {/* Distance */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Distance</label>
          <select
            value={distance}
            onChange={(e) => setDistance(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm"
          >
            <option value={10}>10 miles</option>
            <option value={25}>25 miles</option>
            <option value={50}>50 miles</option>
            <option value={100}>100 miles</option>
            <option value={200}>200 miles</option>
            <option value={9999}>Any distance</option>
          </select>
        </div>

        {/* Sort By */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm"
          >
            <option value="suggested">Suggested</option>
            <option value="distance">Distance: Nearest First</option>
            <option value="newest">Date Listed: Newest First</option>
            <option value="price_asc">Price: Lowest First</option>
            <option value="price_desc">Price: Highest First</option>
          </select>
        </div>

        {/* Price Range */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">$</span>
            <input
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(Number(e.target.value))}
              placeholder="Min"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm"
            />
            <span className="text-gray-500">-</span>
            <input
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Max"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary text-sm"
            />
          </div>
        </div>

        {/* Apply Button */}
        <button
          onClick={fetchListings}
          className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-800 transition-colors text-sm"
        >
          Apply Filters
        </button>

        {/* Category Buttons */}
        <div className="mt-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Categories</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => { setSelectedCategory(''); fetchListings(); }}
              className={`px-2.5 py-1.5 text-xs rounded-full transition-colors ${
                selectedCategory === ''
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => { setSelectedCategory(cat.value); fetchListings(); }}
                className={`px-2.5 py-1.5 text-xs rounded-full transition-colors ${
                  selectedCategory === cat.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6">
        {/* Results Count */}
        <div className="mb-6">
          {loading ? (
            <p className="text-gray-500">Loading listings...</p>
          ) : (
            <p className="text-gray-700 font-medium">
              Found {listings.length} listing{listings.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-300"></div>
                <div className="p-4 space-y-2">
                  <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-300 rounded w-20"></div>
                    <div className="h-4 bg-gray-300 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No listings found</p>
            {searchTerm || selectedCategory ? (
              <Link to="/listings" className="text-primary hover:underline">Clear filters</Link>
            ) : (
              <Link to="/create-listing" className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors">
                Be the first to list an item!
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-8">
            {listings.map(listing => (
              <Link key={listing._id} to={`/listings/${listing._id}`} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden group">
                {/* Listing Image */}
                <div className="h-48 bg-gray-200 flex items-center justify-center text-gray-400 relative overflow-hidden">
                  {listing.images && listing.images.length > 0 && listing.images[0].url ? (
                    <>
                      <img
                        src={listing.images[0].thumbnailUrl || `http://localhost:5000${listing.images[0].url}`}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          if (!e.currentTarget.dataset.fallback) {
                            e.currentTarget.dataset.fallback = "true";
                            e.currentTarget.src = `http://localhost:5000${listing.images[0].url}`;
                          }
                        }}
                      />
                      {listing.images.length > 1 && (
                        <span className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                          +{listing.images.length - 1} more
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="text-center">
                      <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>No image</span>
                    </div>
                  )}
                </div>

                {/* Listing Info */}
                <div className="px-4 pb-3">
                  <p className="text-lg font-bold text-secondary mb-1">
                    ${listing.price?.toLocaleString() || 'Free'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {listing.location?.city && listing.location?.state
                      ? `${listing.location.city}, ${listing.location.state}`
                      : 'Location not specified'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default BrowseListings;
